


import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';

import * as XLSX from 'xlsx';
import { Download, Database, CheckCircle, XCircle, Play, RotateCcw, FileSpreadsheet, RefreshCw, AlertCircle } from 'lucide-react';

const InventoryMigration = () => {
  const [oldData, setOldData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [migrationStatus, setMigrationStatus] = useState({});
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [stats, setStats] = useState({ total: 0, migrated: 0, skipped: 0, errors: 0 });
  const [showPreview, setShowPreview] = useState(false);

  // Conversion function
  const convertDataFormat = (oldItem) => {
    if (oldItem.units === 0) {
      return null;
    }

    return {
      brandId: oldItem.brandId,
      createdAt: oldItem.createdAt,
      lastUpdated: oldItem.updatedAt,
      sku: oldItem.sku,
      totalUnits: oldItem.units,
      inventoryEntries: [
        {
          createdAt: oldItem.createdAt,
          originalOrderId: oldItem.originalOrderId,
          units: oldItem.units,
          updatedAt: oldItem.updatedAt
        }
      ]
    };
  };

  // Fetch old data from Firestore
  const fetchOldData = async () => {
    setLoading(true);
    try {
      console.log('Starting RTO inventory data fetch...');
      
      // Fetch all existing RTO inventory data (old format)
      const inventoryRef = collection(db, 'RTO_inventory');
      const inventorySnapshot = await getDocs(inventoryRef);
      
      const data = [];
      inventorySnapshot.forEach((doc) => {
        data.push({ id: doc.id, ...doc.data() });
      });
      
      console.log(`Fetched ${data.length} items from RTO_inventory`);
      setOldData(data);
      setStats({ total: data.length, migrated: 0, skipped: 0, errors: 0 });
      setMigrationStatus({});
      
    } catch (error) {
      console.error('Error fetching data:', error);
      alert('Error fetching data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Download old data as Excel
  const downloadAsExcel = () => {
    if (oldData.length === 0) {
      alert('No data to download. Please fetch data first.');
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(oldData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'RTO_Inventory_Old');
    
    const fileName = `RTO_Inventory_Old_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    console.log(`Downloaded ${fileName}`);
  };

  // Download converted data preview as Excel
  const downloadConvertedPreview = () => {
    if (oldData.length === 0) {
      alert('No data to preview. Please fetch data first.');
      return;
    }

    const convertedData = oldData.map(item => {
      const converted = convertDataFormat(item);
      return converted || { ...item, conversionStatus: 'SKIPPED - Units is 0' };
    });

    const worksheet = XLSX.utils.json_to_sheet(convertedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'RTO_Inventory_New_Format');
    
    const fileName = `RTO_Inventory_New_Format_Preview_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    console.log(`Downloaded preview ${fileName}`);
  };

  // Migrate single item
  const migrateSingleItem = async (item) => {
    try {
      const convertedData = convertDataFormat(item);
      
      if (!convertedData) {
        setMigrationStatus(prev => ({ ...prev, [item.id]: { status: 'skipped', message: 'Units is 0' } }));
        setStats(prev => ({ ...prev, skipped: prev.skipped + 1 }));
        return;
      }

      // Update the document with new format in Firestore
      const docRef = doc(db, 'RTO_inventory', item.id);
      await setDoc(docRef, convertedData);
      
      console.log(`Successfully migrated item ${item.id} (SKU: ${item.sku})`);
      
      setMigrationStatus(prev => ({ 
        ...prev, 
        [item.id]: { status: 'success', message: 'Migrated successfully' } 
      }));
      setStats(prev => ({ ...prev, migrated: prev.migrated + 1 }));
      
    } catch (error) {
      console.error(`Error migrating item ${item.id}:`, error);
      setMigrationStatus(prev => ({ 
        ...prev, 
        [item.id]: { status: 'error', message: error.message } 
      }));
      setStats(prev => ({ ...prev, errors: prev.errors + 1 }));
    }
  };

  // Migrate all items
  const migrateAllItems = async () => {
    if (oldData.length === 0) {
      alert('No data to migrate. Please fetch data first.');
      return;
    }

    if (!(`Are you sure you want to migrate all ${oldData.length} items? This will permanently change the data structure.`)) {
      return;
    }

    setLoading(true);
    setMigrationStatus({});
    setStats({ total: oldData.length, migrated: 0, skipped: 0, errors: 0 });

    console.log('Starting migration of all items...');
    
    for (const item of oldData) {
      await migrateSingleItem(item);
      // Small delay to prevent overwhelming Firestore
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    setLoading(false);
    const finalStats = stats;
    console.log('Migration completed:', finalStats);
    alert(`Migration completed!\nMigrated: ${finalStats.migrated}\nSkipped: ${finalStats.skipped}\nErrors: ${finalStats.errors}`);
  };

  // Migrate selected items
  const migrateSelectedItems = async () => {
    if (selectedItems.size === 0) {
      alert('No items selected for migration.');
      return;
    }

    if (!(`Are you sure you want to migrate ${selectedItems.size} selected items?`)) {
      return;
    }

    setLoading(true);
    const selectedData = oldData.filter(item => selectedItems.has(item.id));
    
    console.log(`Starting migration of ${selectedData.length} selected items...`);
    
    for (const item of selectedData) {
      await migrateSingleItem(item);
      // Small delay to prevent overwhelming Firestore
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    setLoading(false);
    console.log('Selected items migration completed');
  };

  // Toggle item selection
  const toggleItemSelection = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  // Select all items
  const selectAllItems = () => {
    if (selectedItems.size === oldData.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(oldData.map(item => item.id)));
    }
  };

  // Reset migration status
  const resetMigration = () => {
    setMigrationStatus({});
    setSelectedItems(new Set());
    setStats({ total: oldData.length, migrated: 0, skipped: 0, errors: 0 });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'error':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'skipped':
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'text-green-600 bg-green-50';
      case 'error':
        return 'text-red-600 bg-red-50';
      case 'skipped':
        return 'text-yellow-600 bg-yellow-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  // Preview conversion for a single item
  const previewConversion = (item) => {
    const converted = convertDataFormat(item);
    return {
      original: item,
      converted: converted,
      willSkip: converted === null
    };
  };

  useEffect(() => {
    fetchOldData();
  }, []);

  return (
    <div className="max-w-7xl mx-auto p-6 bg-white">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">RTO Inventory Migration Tool</h1>
        <p className="text-gray-600">Migrate RTO inventory data from old format to new format with inventoryEntries array</p>
      </div>

      {/* Stats Card */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-blue-50 p-4 rounded-lg border">
          <div className="flex items-center">
            <Database className="w-5 h-5 text-blue-500 mr-2" />
            <span className="text-sm font-medium text-blue-700">Total Items</span>
          </div>
          <p className="text-2xl font-bold text-blue-900">{stats.total}</p>
        </div>
        
        <div className="bg-green-50 p-4 rounded-lg border">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
            <span className="text-sm font-medium text-green-700">Migrated</span>
          </div>
          <p className="text-2xl font-bold text-green-900">{stats.migrated}</p>
        </div>
        
        <div className="bg-yellow-50 p-4 rounded-lg border">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-yellow-500 mr-2" />
            <span className="text-sm font-medium text-yellow-700">Skipped</span>
          </div>
          <p className="text-2xl font-bold text-yellow-900">{stats.skipped}</p>
        </div>
        
        <div className="bg-red-50 p-4 rounded-lg border">
          <div className="flex items-center">
            <XCircle className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-sm font-medium text-red-700">Errors</span>
          </div>
          <p className="text-2xl font-bold text-red-900">{stats.errors}</p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-4 mb-8">
        <button
          onClick={fetchOldData}
          disabled={loading}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Fetching...' : 'Refresh Data'}
        </button>

        <button
          onClick={downloadAsExcel}
          disabled={oldData.length === 0}
          className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
        >
          <FileSpreadsheet className="w-4 h-4 mr-2" />
          Download Old Data (Excel)
        </button>

        <button
          onClick={downloadConvertedPreview}
          disabled={oldData.length === 0}
          className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
        >
          <Download className="w-4 h-4 mr-2" />
          Preview New Format (Excel)
        </button>

        <button
          onClick={migrateAllItems}
          disabled={loading || oldData.length === 0}
          className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
        >
          <Play className="w-4 h-4 mr-2" />
          Migrate All Items
        </button>

        <button
          onClick={migrateSelectedItems}
          disabled={loading || selectedItems.size === 0}
          className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
        >
          <Play className="w-4 h-4 mr-2" />
          Migrate Selected ({selectedItems.size})
        </button>

        <button
          onClick={resetMigration}
          className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          Reset Status
        </button>
      </div>

      {/* Selection Controls */}
      {oldData.length > 0 && (
        <div className="flex items-center justify-between mb-4 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-4">
            <button
              onClick={selectAllItems}
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              {selectedItems.size === oldData.length ? 'Deselect All' : 'Select All'}
            </button>
            <span className="text-gray-600">
              {selectedItems.size} of {oldData.length} items selected
            </span>
          </div>
          
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="text-purple-600 hover:text-purple-800 font-medium"
          >
            {showPreview ? 'Hide Preview' : 'Show Conversion Preview'}
          </button>
        </div>
      )}

      {/* Data Table */}
      {oldData.length > 0 ? (
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={selectedItems.size === oldData.length && oldData.length > 0}
                      onChange={selectAllItems}
                      className="rounded"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Brand ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Units</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                  {showPreview && (
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Conversion Preview</th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {oldData.map((item) => {
                  const status = migrationStatus[item.id];
                  const preview = showPreview ? previewConversion(item) : null;
                  
                  return (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedItems.has(item.id)}
                          onChange={() => toggleItemSelection(item.id)}
                          className="rounded"
                        />
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {status ? (
                          <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(status.status)}`}>
                            {getStatusIcon(status.status)}
                            <span className="ml-1">{status.status.toUpperCase()}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400">Pending</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.sku}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.brandId ? item.brandId.substring(0, 20) + '...' : 'N/A'}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className={`font-medium ${item.units === 0 ? 'text-yellow-600' : 'text-gray-900'}`}>
                          {item.units}
                        </span>
                        {item.units === 0 && <span className="text-xs text-yellow-600 ml-1">(Will Skip)</span>}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.originalOrderId}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(item.createdAt).toLocaleDateString()}
                      </td>
                      {showPreview && (
                        <td className="px-4 py-4 text-sm">
                          {preview.willSkip ? (
                            <span className="text-yellow-600 font-medium">Will be skipped (units = 0)</span>
                          ) : (
                            <div className="space-y-1">
                              <div className="text-green-600">✓ Will convert to new format</div>
                              <div className="text-xs text-gray-500">totalUnits: {preview.converted.totalUnits}</div>
                              <div className="text-xs text-gray-500">inventoryEntries: 1 item</div>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <Database className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No inventory data</h3>
          <p className="mt-1 text-sm text-gray-500">
            {loading ? 'Loading data from Firestore...' : 'Click "Refresh Data" to load inventory items'}
          </p>
        </div>
      )}

      {/* Migration Instructions */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-blue-900 mb-4">Migration Instructions</h3>
        <div className="space-y-3 text-blue-800">
          <div className="flex items-start">
            <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium mr-3">1</span>
            <p><strong>Download Old Data:</strong> Export current data as Excel backup before migration</p>
          </div>
          <div className="flex items-start">
            <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium mr-3">2</span>
            <p><strong>Preview New Format:</strong> Check how data will look after conversion</p>
          </div>
          <div className="flex items-start">
            <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium mr-3">3</span>
            <p><strong>Test Migration:</strong> Select a few items to test migration first</p>
          </div>
          <div className="flex items-start">
            <span className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium mr-3">4</span>
            <p><strong>Full Migration:</strong> After testing, run full migration on all items</p>
          </div>
        </div>
        
        <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded">
          <p className="text-yellow-800"><strong>Note:</strong> Items with 0 units will be automatically skipped. All original timestamps will be preserved.</p>
        </div>
      </div>
    </div>
  );
};

export default InventoryMigration;